import { isApiError } from "@bw/shared/api";
import type { KioskRedReason, KioskScanResult } from "@bw/shared/types/generated";

/**
 * What the entrance kiosk shows after a scan (#2691). Green or red only; the
 * kiosk never charges and has no amber state. A scan that cannot reach the
 * server is a neutral "see the front desk", never a guess at green or red.
 */

/** How long a result stays up before the kiosk is ready again. */
export const KIOSK_RESULT_MS = 2_500;
/** The neutral "couldn't check" screen stays a little longer. */
export const KIOSK_PROBLEM_MS = 3_500;

export type RedIcon = "block" | "timer_off" | "wrong_location" | "event_busy" | "waiver";

export interface RedCopy {
  icon: RedIcon;
  title: string;
  detail: string;
}

/** Plain words for each red reason, readable across the room. */
export function redReasonCopy(reason: KioskRedReason): RedCopy {
  switch (reason) {
    case "already_used":
      return {
        icon: "block",
        title: "Already used",
        detail: "This code has already checked someone in. Please see the front desk.",
      };
    case "expired_code":
      // The player fixes this themselves (approved frame): no desk visit.
      return {
        icon: "timer_off",
        title: "Expired code",
        detail: "Refresh it in the app and scan again.",
      };
    case "not_valid_here":
      return {
        icon: "wrong_location",
        title: "Not valid here",
        detail: "This code isn't for this entrance. Please see the front desk.",
      };
    case "not_valid_now":
      return {
        icon: "event_busy",
        title: "Not valid now",
        detail: "This code can't be used right now. Please see the front desk.",
      };
    case "waiver_needed":
      return {
        icon: "waiver",
        title: "Waiver needed",
        detail: "Sign it in the app or at the desk. Please see the front desk.",
      };
  }
}

/** The green headline: what the scan checked in and unlocked. */
export function greenHeadline(result: KioskScanResult): string {
  if (result.needs_staff_confirmation) return "Checked in · See the front desk to start";
  if (result.unlocked === "drop_in_queue") return "Checked in · Drop-in unlocked";
  if (result.unlocked === "court_booking") return "Checked in · Court booking";
  return "Checked in";
}

/** "7 visits left" for a package; nothing for an unlimited membership or booking. */
export function unitsLeftLine(unitsLeft: number | null | undefined): string | null {
  if (unitsLeft === null || unitsLeft === undefined) return null;
  if (unitsLeft === 0) return "No visits left on this pass";
  return `${unitsLeft} ${unitsLeft === 1 ? "visit" : "visits"} left`;
}

/** "7:30 PM" in the facility's timezone (the tablet's own when unknown). */
export function formatKioskTime(value: Date | string, timeZone?: string | null): string {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "";
  const options: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };
  try {
    return new Intl.DateTimeFormat("en-US", { ...options, timeZone: timeZone ?? undefined }).format(date);
  } catch {
    // An unknown zone name falls back to the tablet's clock, never a blank.
    return new Intl.DateTimeFormat("en-US", options).format(date);
  }
}

/**
 * The line under the green headline (approved frame): "10-Visit Card · 7 of
 * 10 left" for a pass, the plan name alone for an unlimited membership, and
 * "Court 3 · 7:30 PM" for a booking.
 */
export function greenDetailLine(result: KioskScanResult, timeZone?: string | null): string | null {
  if (result.unlocked === "court_booking") {
    const time = result.starts_at ? formatKioskTime(result.starts_at, timeZone) : "";
    return [result.court_name, time].filter(Boolean).join(" · ") || null;
  }
  const units = result.units_left;
  if (units === null || units === undefined) return result.plan_name ?? null;
  const count = result.total_visits ? `${units} of ${result.total_visits} left` : unitsLeftLine(units);
  return result.plan_name ? `${result.plan_name} · ${count}` : count;
}

/** Up to this many visits are drawn as dots; larger cards show the line only. */
export const MAX_KIOSK_VISIT_DOTS = 20;

/** One dot per visit on the pass, filled for each one still left. */
export function greenVisitDots(result: KioskScanResult): boolean[] | null {
  const total = result.total_visits;
  const left = result.units_left;
  if (!total || total > MAX_KIOSK_VISIT_DOTS || left === null || left === undefined) return null;
  return Array.from({ length: total }, (_, index) => index < Math.max(0, Math.min(total, left)));
}

/** The quiet next step under the green result. */
export function greenNextStep(result: KioskScanResult): string | null {
  if (result.needs_staff_confirmation) return null;
  if (result.unlocked === "drop_in_queue") return "Join the queue in the app";
  if (result.unlocked === "court_booking") return "Enjoy your game";
  return null;
}

export type ScanFailure =
  /** The device session is gone (revoked, expired): pair again. */
  | { kind: "signed_out" }
  /** Retry the same scan id: the server asked for it, or the network failed. */
  | { kind: "retry" }
  /** Check-in was turned off for the facility while the kiosk was running. */
  | { kind: "off" }
  /** A definitive local refusal of an unreadable code. */
  | { kind: "red"; reason: KioskRedReason }
  /** Anything else: neutral, see the front desk. */
  | { kind: "problem" };

/** Classify a failed POST /kiosk/scans. */
export function classifyScanFailure(error: unknown): ScanFailure {
  if (isApiError(error)) {
    if (error.status === 401 || error.status === 403) return { kind: "signed_out" };
    if (error.status === 409 && error.code === "kiosk_off") return { kind: "off" };
    if (error.status === 503 || error.status === 502 || error.status === 504) {
      return { kind: "retry" };
    }
    // 422: the decoded text is not a check-in code at all (empty or huge).
    if (error.status === 422) return { kind: "red", reason: "not_valid_here" };
    return { kind: "problem" };
  }
  // fetch() rejects with a TypeError when the network is unreachable; the
  // scan may or may not have landed, so it is retried under the same scan id.
  return { kind: "retry" };
}

/** Backoff before retry `attempt` (1-based) of the same scan id. */
export function scanRetryDelayMs(attempt: number): number {
  return Math.min(4_000, 500 * 2 ** (attempt - 1));
}

/** Retries of one scan before showing "see the front desk". */
export const MAX_SCAN_RETRIES = 3;

export type ExchangeFailure = "invalid" | "kiosk_off" | "rate_limited" | "offline" | "problem";

/** Classify a failed setup-code exchange. */
export function classifyExchangeFailure(error: unknown): ExchangeFailure {
  if (isApiError(error)) {
    if (error.status === 409 && error.code === "kiosk_off") return "kiosk_off";
    if ([400, 401, 403, 404, 409, 422].includes(error.status)) return "invalid";
    if (error.status === 429) return "rate_limited";
    return "problem";
  }
  return "offline";
}

export function exchangeFailureMessage(failure: ExchangeFailure): string {
  switch (failure) {
    case "invalid":
      return "That setup code is invalid, used or expired. Ask for a new one in Front desk › Devices.";
    case "kiosk_off":
      return "Kiosk check-in is off at this facility. Turn it on in Front desk › Devices, then start a new kiosk.";
    case "rate_limited":
      return "Too many tries. Wait a minute, then tap Scan setup code again.";
    case "offline":
      return "This tablet can't reach Badminton Warriors. Check the Wi-Fi, then try again.";
    case "problem":
      return "The kiosk couldn't be paired just now. Try again.";
  }
}
